/**
 * ESG Engagement Insights Component - Modern Redesign
 * 
 * Comprehensive stakeholder engagement analytics providing:
 * - Real-time engagement metrics
 * - Multi-channel communication tracking
 * - Social impact measurement
 * - Stakeholder satisfaction analysis
 * - ROI and value creation tracking
 * - Predictive engagement analytics
 * 
 * @version 2.0.0
 * @redesigned 2025-10-06
 */

import React, { useState, useEffect } from 'react';
import HandshakeIcon from '../../../components/HandshakeIcon';
import esgEngagementInsightsService from '../../../services/esg/esgEngagementInsightsService';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  PieChart, Pie, Cell, ResponsiveContainer, CartesianGrid, XAxis, YAxis, Tooltip, Legend, ScatterChart, Scatter
} from 'recharts';

const ESGEngagementInsights = () => {
  const [engagementData, setEngagementData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedTimeframe, setSelectedTimeframe] = useState('12M');

  useEffect(() => {
    const fetchEngagementData = async () => {
      try {
        setLoading(true);
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const overview = esgEngagementInsightsService.getEngagementOverview();
        const stakeholders = esgEngagementInsightsService.getStakeholderInsights();
        const channels = esgEngagementInsightsService.getChannelPerformance();
        const socialImpact = esgEngagementInsightsService.getSocialImpactAssessment();
        const predictive = esgEngagementInsightsService.getPredictiveAnalytics();
        const roiAnalysis = esgEngagementInsightsService.getROIAnalysis();

        setEngagementData({
          overview,
          stakeholders,
          channels,
          socialImpact,
          predictive,
          roiAnalysis
        });
      } catch (err) {
        console.error('Error fetching engagement insights:', err);
        setError('Failed to load engagement insights data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchEngagementData();

    const unsubscribe = esgEngagementInsightsService.subscribe(() => {
      fetchEngagementData();
    });

    return () => unsubscribe();
  }, []);

  const handleRefresh = () => {
    esgEngagementInsightsService.refreshData();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-purple-600 mb-4"></div>
          <p className="text-gray-600 text-lg font-medium">Loading engagement insights...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-red-600 text-2xl">⚠</span>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Data</h2>
          <p className="text-gray-600 mb-6">{error}</p>
          <button 
            onClick={() => window.location.reload()}
            className="bg-purple-600 text-white px-6 py-3 rounded-xl hover:bg-purple-700 transition-colors font-medium"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const renderOverview = () => {
    const kpiCards = [
      { 
        label: 'Engagement Score', 
        value: '88.5', 
        change: '+4.2%',
        trend: 'up',
        icon: '🎯',
        color: 'from-purple-500 to-indigo-600'
      },
      { 
        label: 'Active Stakeholders', 
        value: '12.5K', 
        change: '+850',
        trend: 'up',
        icon: '👥',
        color: 'from-blue-500 to-cyan-600'
      },
      { 
        label: 'Satisfaction Rate', 
        value: '92%', 
        change: '+3%',
        trend: 'up',
        icon: '⭐',
        color: 'from-green-500 to-emerald-600'
      },
      { 
        label: 'Response Rate', 
        value: '78%', 
        change: '+5%',
        trend: 'up',
        icon: '📊',
        color: 'from-orange-500 to-red-600'
      }
    ];

    const engagementTrend = [
      { month: 'Jan', engagement: 82, satisfaction: 88, response: 72 },
      { month: 'Feb', engagement: 83, satisfaction: 89, response: 73 },
      { month: 'Mar', engagement: 84, satisfaction: 89, response: 74 },
      { month: 'Apr', engagement: 85, satisfaction: 90, response: 75 },
      { month: 'May', engagement: 86, satisfaction: 91, response: 76 },
      { month: 'Jun', engagement: 87, satisfaction: 91, response: 77 },
      { month: 'Jul', engagement: 87, satisfaction: 92, response: 77 },
      { month: 'Aug', engagement: 88, satisfaction: 92, response: 78 },
      { month: 'Sep', engagement: 88, satisfaction: 92, response: 78 }
    ];

    const stakeholderGroups = [
      { name: 'Employees', value: 3500, color: '#8B5CF6' },
      { name: 'Customers', value: 5200, color: '#3B82F6' },
      { name: 'Investors', value: 1800, color: '#10B981' },
      { name: 'Community', value: 2000, color: '#F59E0B' }
    ];

    const channelPerformance = [
      { channel: 'Email', reach: 95, engagement: 72, satisfaction: 88 },
      { channel: 'Social', reach: 88, engagement: 85, satisfaction: 82 },
      { channel: 'Events', reach: 65, engagement: 92, satisfaction: 95 },
      { channel: 'Portal', reach: 78, engagement: 80, satisfaction: 86 },
      { channel: 'Survey', reach: 70, engagement: 88, satisfaction: 90 }
    ];

    return (
      <div className="space-y-6">
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {kpiCards.map((kpi, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${kpi.color} flex items-center justify-center text-2xl shadow-lg`}>
                  {kpi.icon}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  kpi.trend === 'up' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                }`}>
                  {kpi.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{kpi.label}</h3>
              <p className="text-3xl font-bold text-gray-900">{kpi.value}</p>
            </div>
          ))}
        </div>

        {/* Engagement Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Engagement Metrics Trend</h3>
              <p className="text-gray-600 text-sm mt-1">Performance across key engagement dimensions</p>
            </div>
            <div className="flex gap-2">
              {['3M', '6M', '12M', 'YTD'].map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedTimeframe(period)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    selectedTimeframe === period
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {period}
                </button>
              ))}
            </div>
          </div>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={engagementTrend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="engagement" stroke="#8B5CF6" strokeWidth={3} name="Engagement Score" />
              <Line type="monotone" dataKey="satisfaction" stroke="#10B981" strokeWidth={2} name="Satisfaction %" />
              <Line type="monotone" dataKey="response" stroke="#3B82F6" strokeWidth={2} name="Response Rate %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Stakeholder Distribution & Channel Performance */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Stakeholder Distribution */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Stakeholder Distribution</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={stakeholderGroups}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stakeholderGroups.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Channel Performance */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Channel Performance Radar</h3>
            <ResponsiveContainer width="100%" height={300}>
              <RadarChart data={channelPerformance}>
                <PolarGrid stroke="#E5E7EB" />
                <PolarAngleAxis dataKey="channel" stroke="#6B7280" />
                <PolarRadiusAxis stroke="#6B7280" />
                <Radar name="Reach" dataKey="reach" stroke="#3B82F6" fill="#3B82F6" fillOpacity={0.3} />
                <Radar name="Engagement" dataKey="engagement" stroke="#8B5CF6" fill="#8B5CF6" fillOpacity={0.3} />
                <Radar name="Satisfaction" dataKey="satisfaction" stroke="#10B981" fill="#10B981" fillOpacity={0.3} />
                <Legend />
                <Tooltip />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Insights */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 rounded-2xl shadow-lg p-6 text-white">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl flex-shrink-0">
              💡
            </div>
            <div>
              <h3 className="text-xl font-bold mb-2">Key Insights</h3>
              <ul className="space-y-1 text-white/90">
                <li>• Overall engagement score increased by 4.2% to 88.5, exceeding industry benchmark</li>
                <li>• Active stakeholder base grew by 850 users, with highest growth in customer segment</li>
                <li>• Event-based channels show 92% engagement rate, 7% above average</li>
                <li>• Satisfaction rates improved across all stakeholder groups, reaching 92% overall</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderStakeholders = () => {
    const stakeholderMetrics = [
      { group: 'Employees', engagement: 88, satisfaction: 91, reach: 3500, growth: '+12%', color: 'from-purple-500 to-indigo-600' },
      { group: 'Customers', engagement: 85, satisfaction: 89, reach: 5200, growth: '+18%', color: 'from-blue-500 to-cyan-600' },
      { group: 'Investors', engagement: 92, satisfaction: 94, reach: 1800, growth: '+8%', color: 'from-green-500 to-emerald-600' },
      { group: 'Community', engagement: 78, satisfaction: 82, reach: 2000, growth: '+15%', color: 'from-orange-500 to-red-600' }
    ];

    const satisfactionTrends = [
      { month: 'Jan', employees: 88, customers: 85, investors: 90, community: 78 },
      { month: 'Feb', employees: 89, customers: 86, investors: 91, community: 79 },
      { month: 'Mar', employees: 89, customers: 87, investors: 92, community: 80 },
      { month: 'Apr', employees: 90, customers: 88, investors: 92, community: 81 },
      { month: 'May', employees: 90, customers: 88, investors: 93, community: 81 },
      { month: 'Jun', employees: 91, customers: 89, investors: 94, community: 82 }
    ];

    const topConcerns = [
      { concern: 'Sustainability Initiatives', priority: 'High', affected: 'All Groups', status: 'In Progress' },
      { concern: 'Transparency', priority: 'High', affected: 'Investors, Community', status: 'Ongoing' },
      { concern: 'Work-Life Balance', priority: 'Medium', affected: 'Employees', status: 'Addressed' },
      { concern: 'Product Quality', priority: 'High', affected: 'Customers', status: 'Monitoring' },
      { concern: 'Community Impact', priority: 'Medium', affected: 'Community', status: 'In Progress' }
    ];

    const getPriorityColor = (priority) => {
      switch(priority) {
        case 'High': return 'bg-red-100 text-red-700 border-red-200';
        case 'Medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
        case 'Low': return 'bg-green-100 text-green-700 border-green-200';
        default: return 'bg-gray-100 text-gray-700 border-gray-200';
      }
    };

    return (
      <div className="space-y-6">
        {/* Stakeholder Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {stakeholderMetrics.map((group, index) => (
            <div key={index} className={`bg-gradient-to-br ${group.color} rounded-2xl shadow-lg p-6 text-white`}>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">{group.group}</h3>
                <span className="bg-white/20 px-3 py-1 rounded-full text-xs font-semibold">
                  {group.growth}
                </span>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-white/70 mb-1">Engagement</p>
                  <p className="text-2xl font-bold">{group.engagement}%</p>
                </div>
                <div>
                  <p className="text-xs text-white/70 mb-1">Satisfaction</p>
                  <p className="text-2xl font-bold">{group.satisfaction}%</p>
                </div>
              </div>
              <div className="pt-4 border-t border-white/20">
                <p className="text-sm text-white/80">Active Members</p>
                <p className="text-xl font-bold">{group.reach.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Satisfaction Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Stakeholder Satisfaction Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={satisfactionTrends}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="employees" stroke="#8B5CF6" strokeWidth={2} name="Employees" />
              <Line type="monotone" dataKey="customers" stroke="#3B82F6" strokeWidth={2} name="Customers" />
              <Line type="monotone" dataKey="investors" stroke="#10B981" strokeWidth={2} name="Investors" />
              <Line type="monotone" dataKey="community" stroke="#F59E0B" strokeWidth={2} name="Community" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top Concerns & Issues */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Top Stakeholder Concerns</h3>
          <div className="space-y-3">
            {topConcerns.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="font-semibold text-gray-900">{item.concern}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold border ${getPriorityColor(item.priority)}`}>
                        {item.priority}
                      </span>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-gray-600">
                      <span>👥 {item.affected}</span>
                      <span>📊 Status: <strong className="text-purple-600">{item.status}</strong></span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderChannels = () => {
    const channelMetrics = [
      { channel: 'Email Campaigns', reach: 12500, engagement: 72, satisfaction: 88, roi: 245, icon: '📧' },
      { channel: 'Social Media', reach: 45000, engagement: 85, satisfaction: 82, roi: 320, icon: '📱' },
      { channel: 'Events & Webinars', reach: 3200, engagement: 92, satisfaction: 95, roi: 180, icon: '🎤' },
      { channel: 'Customer Portal', reach: 8500, engagement: 80, satisfaction: 86, roi: 210, icon: '🌐' },
      { channel: 'Surveys & Feedback', reach: 6800, engagement: 88, satisfaction: 90, roi: 165, icon: '📊' }
    ];

    const channelComparison = [
      { channel: 'Email', current: 72, target: 70, benchmark: 65 },
      { channel: 'Social', current: 85, target: 80, benchmark: 75 },
      { channel: 'Events', current: 92, target: 85, benchmark: 80 },
      { channel: 'Portal', current: 80, target: 75, benchmark: 70 },
      { channel: 'Survey', current: 88, target: 85, benchmark: 78 }
    ];

    const contentPerformance = [
      { type: 'Sustainability Updates', shares: 2850, likes: 12400, comments: 850, engagement: 92 },
      { type: 'Product News', shares: 3200, likes: 15600, comments: 1200, engagement: 88 },
      { type: 'Community Stories', shares: 2100, likes: 9800, comments: 650, engagement: 85 },
      { type: 'ESG Reports', shares: 1800, likes: 7200, comments: 420, engagement: 78 },
      { type: 'Event Invitations', shares: 1500, likes: 8500, comments: 380, engagement: 82 }
    ];

    return (
      <div className="space-y-6">
        {/* Channel Performance Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {channelMetrics.map((channel, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-xl flex items-center justify-center text-2xl">
                  {channel.icon}
                </div>
                <h3 className="font-bold text-gray-900">{channel.channel}</h3>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-gray-600 mb-1">Reach</p>
                  <p className="text-lg font-bold text-gray-900">{channel.reach.toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Engagement</p>
                  <p className="text-lg font-bold text-purple-600">{channel.engagement}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">Satisfaction</p>
                  <p className="text-lg font-bold text-green-600">{channel.satisfaction}%</p>
                </div>
                <div>
                  <p className="text-xs text-gray-600 mb-1">ROI</p>
                  <p className="text-lg font-bold text-blue-600">{channel.roi}%</p>
                </div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                  style={{ width: `${channel.engagement}%` }}
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Channel Comparison Chart */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Channel Performance vs Targets & Benchmarks</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={channelComparison}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="channel" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="current" fill="#8B5CF6" name="Current Performance" radius={[8, 8, 0, 0]} />
              <Bar dataKey="target" fill="#3B82F6" name="Target" radius={[8, 8, 0, 0]} />
              <Bar dataKey="benchmark" fill="#E5E7EB" name="Industry Benchmark" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Content Performance */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Top Performing Content</h3>
          <div className="space-y-4">
            {contentPerformance.map((content, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-purple-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{content.type}</h4>
                  <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold">
                    {content.engagement}% engagement
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <span>🔄 {content.shares.toLocaleString()} shares</span>
                  <span>❤️ {content.likes.toLocaleString()} likes</span>
                  <span>💬 {content.comments.toLocaleString()} comments</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderImpact = () => {
    const impactMetrics = [
      { category: 'Social Value', value: '$2.4M', growth: '+18%', icon: 'handshake', color: 'from-blue-500 to-cyan-600' },
      { category: 'Environmental Impact', value: '850T CO₂', growth: '-22%', icon: '🌱', color: 'from-green-500 to-emerald-600' },
      { category: 'Community Reach', value: '125K', growth: '+35%', icon: '🏘️', color: 'from-purple-500 to-indigo-600' },
      { category: 'Lives Impacted', value: '45K', growth: '+28%', icon: '❤️', color: 'from-pink-500 to-rose-600' }
    ];

    const impactTrends = [
      { quarter: 'Q1', social: 1.8, environmental: 1200, community: 92, lives: 35 },
      { quarter: 'Q2', social: 2.0, environmental: 1050, community: 105, lives: 38 },
      { quarter: 'Q3', social: 2.2, environmental: 920, community: 115, lives: 42 },
      { quarter: 'Q4', social: 2.4, environmental: 850, community: 125, lives: 45 }
    ];

    const sdgAlignment = [
      { goal: 'SDG 3: Health', alignment: 88, impact: 'High', progress: 85 },
      { goal: 'SDG 4: Education', alignment: 82, impact: 'High', progress: 78 },
      { goal: 'SDG 8: Work & Growth', alignment: 90, impact: 'Medium', progress: 88 },
      { goal: 'SDG 12: Consumption', alignment: 85, impact: 'High', progress: 82 },
      { goal: 'SDG 13: Climate', alignment: 92, impact: 'High', progress: 90 }
    ];

    return (
      <div className="space-y-6">
        {/* Impact Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {impactMetrics.map((metric, index) => (
            <div key={index} className={`bg-gradient-to-br ${metric.color} rounded-2xl shadow-lg p-6 text-white`}>
              <div className="flex items-center justify-between mb-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl">
                  {metric.icon === 'handshake' ? <HandshakeIcon size={28} /> : metric.icon}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  metric.growth.startsWith('+') ? 'bg-white/20' : 'bg-green-500/30'
                }`}>
                  {metric.growth}
                </span>
              </div>
              <h3 className="text-sm text-white/80 mb-2">{metric.category}</h3>
              <p className="text-3xl font-bold">{metric.value}</p>
            </div>
          ))}
        </div>

        {/* Impact Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Social & Environmental Impact Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={impactTrends}>
              <defs>
                <linearGradient id="colorSocial" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#3B82F6" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorCommunity" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="quarter" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Area type="monotone" dataKey="social" stroke="#3B82F6" fillOpacity={1} fill="url(#colorSocial)" name="Social Value ($M)" />
              <Area type="monotone" dataKey="community" stroke="#8B5CF6" fillOpacity={1} fill="url(#colorCommunity)" name="Community Reach (K)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* SDG Alignment */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">UN SDG Alignment & Progress</h3>
          <div className="space-y-4">
            {sdgAlignment.map((sdg, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h4 className="font-semibold text-gray-900">{sdg.goal}</h4>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      sdg.impact === 'High' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                    }`}>
                      {sdg.impact} Impact
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Alignment Score</p>
                    <p className="text-2xl font-bold text-purple-600">{sdg.alignment}%</p>
                  </div>
                </div>
                <div className="relative">
                  <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                      style={{ width: `${sdg.progress}%` }}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-600 mt-1">Progress: {sdg.progress}% to target</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderROI = () => {
    const roiMetrics = [
      { label: 'Total Investment', value: '$3.2M', change: '+15%', icon: '💰' },
      { label: 'Social ROI', value: '245%', change: '+18%', icon: '📈' },
      { label: 'Cost per Engagement', value: '$2.45', change: '-12%', icon: '💎' },
      { label: 'Value Generated', value: '$7.8M', change: '+22%', icon: '✨' }
    ];

    const roiBreakdown = [
      { category: 'Employee Programs', investment: 850, return: 2100, roi: 247 },
      { category: 'Customer Engagement', investment: 1200, return: 2900, roi: 242 },
      { category: 'Community Initiatives', investment: 650, return: 1500, roi: 231 },
      { category: 'Investor Relations', investment: 500, return: 1300, roi: 260 }
    ];

    const roiTrends = [
      { quarter: 'Q1 23', investment: 2.8, return: 6.5, roi: 232 },
      { quarter: 'Q2 23', investment: 2.9, return: 6.8, roi: 234 },
      { quarter: 'Q3 23', investment: 3.0, return: 7.2, roi: 240 },
      { quarter: 'Q4 23', investment: 3.1, return: 7.5, roi: 242 },
      { quarter: 'Q1 24', investment: 3.2, return: 7.8, roi: 245 }
    ];

    return (
      <div className="space-y-6">
        {/* ROI KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {roiMetrics.map((metric, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center text-2xl shadow-lg">
                  {metric.icon === 'handshake' ? <HandshakeIcon size={28} /> : metric.icon}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                  metric.change.startsWith('+') ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                }`}>
                  {metric.change}
                </span>
              </div>
              <h3 className="text-gray-600 text-sm font-medium mb-1">{metric.label}</h3>
              <p className="text-3xl font-bold text-gray-900">{metric.value}</p>
            </div>
          ))}
        </div>

        {/* ROI Breakdown by Category */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">ROI by Engagement Category</h3>
          <div className="space-y-4">
            {roiBreakdown.map((item, index) => (
              <div key={index} className="border border-gray-200 rounded-xl p-4 hover:border-green-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-gray-900">{item.category}</h4>
                  <div className="flex gap-6 text-sm">
                    <span className="text-gray-600">Investment: <strong>${item.investment}K</strong></span>
                    <span className="text-gray-600">Return: <strong>${item.return}K</strong></span>
                    <span className="text-green-600 font-bold text-lg">{item.roi}% ROI</span>
                  </div>
                </div>
                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-green-400 to-emerald-600 rounded-full"
                    style={{ width: `${(item.roi / 260) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ROI Trends */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Investment vs Return Trends</h3>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={roiTrends}>
              <defs>
                <linearGradient id="colorInvestment" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#EF4444" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#EF4444" stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorReturn" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.8}/>
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="quarter" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Area type="monotone" dataKey="investment" stroke="#EF4444" fillOpacity={1} fill="url(#colorInvestment)" name="Investment ($M)" />
              <Area type="monotone" dataKey="return" stroke="#10B981" fillOpacity={1} fill="url(#colorReturn)" name="Return ($M)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* ROI Summary */}
        <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-lg p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Overall Engagement ROI</h3>
              <p className="text-green-100 mb-4">Comprehensive return on stakeholder engagement investments</p>
              <div className="flex gap-8">
                <div>
                  <p className="text-sm text-green-200">Total Invested</p>
                  <p className="text-3xl font-bold">$3.2M</p>
                </div>
                <div>
                  <p className="text-sm text-green-200">Total Return</p>
                  <p className="text-3xl font-bold">$7.8M</p>
                </div>
                <div>
                  <p className="text-sm text-green-200">Net Value</p>
                  <p className="text-3xl font-bold">$4.6M</p>
                </div>
              </div>
            </div>
            <div className="text-center">
              <div className="w-32 h-32 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <div>
                  <p className="text-5xl font-bold">245%</p>
                  <p className="text-sm text-green-200">ROI</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderAnalytics = () => {
    const predictiveMetrics = [
      { metric: 'Next Quarter Engagement', prediction: '90.2%', confidence: '88%', trend: 'up' },
      { metric: 'Stakeholder Growth', prediction: '+1,250', confidence: '85%', trend: 'up' },
      { metric: 'Satisfaction Forecast', prediction: '93%', confidence: '90%', trend: 'up' },
      { metric: 'ROI Projection', prediction: '265%', confidence: '82%', trend: 'up' }
    ];

    const sentimentAnalysis = [
      { category: 'Sustainability', positive: 78, neutral: 18, negative: 4 },
      { category: 'Products', positive: 72, neutral: 22, negative: 6 },
      { category: 'Culture', positive: 85, neutral: 12, negative: 3 },
      { category: 'Leadership', positive: 68, neutral: 25, negative: 7 },
      { category: 'Community', positive: 82, neutral: 15, negative: 3 }
    ];

    const forecastData = [
      { month: 'Sep', actual: 88, forecast: 88, confidence: 95 },
      { month: 'Oct', actual: null, forecast: 89, confidence: 90 },
      { month: 'Nov', actual: null, forecast: 90, confidence: 88 },
      { month: 'Dec', actual: null, forecast: 91, confidence: 85 },
      { month: 'Jan', actual: null, forecast: 92, confidence: 82 },
      { month: 'Feb', actual: null, forecast: 93, confidence: 78 }
    ];

    return (
      <div className="space-y-6">
        {/* Predictive Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {predictiveMetrics.map((item, index) => (
            <div key={index} className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-shadow">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-600">{item.metric}</h3>
                <span className="text-xl text-green-500">↗</span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mb-2">{item.prediction}</p>
              <div className="flex items-center gap-2">
                <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-indigo-600 rounded-full"
                    style={{ width: item.confidence }}
                  ></div>
                </div>
                <span className="text-xs font-semibold text-purple-600">{item.confidence}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Sentiment Analysis */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Sentiment Analysis by Topic</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={sentimentAnalysis} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis type="number" stroke="#6B7280" />
              <YAxis dataKey="category" type="category" stroke="#6B7280" width={120} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Bar dataKey="positive" stackId="a" fill="#10B981" name="Positive" />
              <Bar dataKey="neutral" stackId="a" fill="#F59E0B" name="Neutral" />
              <Bar dataKey="negative" stackId="a" fill="#EF4444" name="Negative" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Engagement Forecast */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-6">Engagement Score Forecast</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={forecastData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
              <XAxis dataKey="month" stroke="#6B7280" />
              <YAxis stroke="#6B7280" />
              <Tooltip 
                contentStyle={{ backgroundColor: '#FFF', border: '1px solid #E5E7EB', borderRadius: '8px' }}
              />
              <Legend />
              <Line type="monotone" dataKey="actual" stroke="#3B82F6" strokeWidth={3} name="Actual" dot={{ r: 5 }} />
              <Line type="monotone" dataKey="forecast" stroke="#8B5CF6" strokeWidth={2} strokeDasharray="5 5" name="Forecast" />
              <Line type="monotone" dataKey="confidence" stroke="#10B981" strokeWidth={1} name="Confidence %" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Analytics Summary */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              🎯
            </div>
            <h3 className="text-lg font-semibold mb-2">Prediction Accuracy</h3>
            <p className="text-4xl font-bold mb-2">88.5%</p>
            <p className="text-sm text-purple-100">Average model confidence</p>
          </div>

          <div className="bg-gradient-to-br from-blue-600 to-cyan-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              📈
            </div>
            <h3 className="text-lg font-semibold mb-2">Growth Trajectory</h3>
            <p className="text-4xl font-bold mb-2">+22%</p>
            <p className="text-sm text-blue-100">Projected annual growth</p>
          </div>

          <div className="bg-gradient-to-br from-green-600 to-emerald-700 rounded-2xl shadow-lg p-6 text-white">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center text-2xl mb-4">
              ✨
            </div>
            <h3 className="text-lg font-semibold mb-2">Sentiment Score</h3>
            <p className="text-4xl font-bold mb-2">77%</p>
            <p className="text-sm text-green-100">Overall positive sentiment</p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-pink-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 via-indigo-600 to-pink-600 text-white shadow-2xl">
        <div className="max-w-7xl mx-auto px-6 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold mb-2">Engagement Insights Dashboard</h1>
              <p className="text-purple-100 text-lg">Comprehensive stakeholder engagement analytics and impact measurement</p>
            </div>
            <div className="flex items-center gap-6">
              <div className="text-right">
                <p className="text-sm text-purple-200">Engagement Score</p>
                <p className="text-3xl font-bold">88.5</p>
              </div>
              <button 
                onClick={handleRefresh}
                className="bg-white/20 backdrop-blur-sm hover:bg-white/30 px-6 py-3 rounded-xl font-medium transition-colors flex items-center gap-2"
              >
                <span>🔄</span> Refresh
              </button>
            </div>
          </div>

          {/* Timeframe Selector */}
          <div className="flex gap-2 mt-6">
            {['3M', '6M', '12M', 'YTD', 'ALL'].map((period) => (
              <button
                key={period}
                onClick={() => setSelectedTimeframe(period)}
                className={`px-4 py-2 rounded-lg font-medium transition-all ${
                  selectedTimeframe === period
                    ? 'bg-white text-purple-600 shadow-lg'
                    : 'bg-white/10 text-white hover:bg-white/20'
                }`}
              >
                {period}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex gap-1">
            {[
              { id: 'overview', label: 'Overview', icon: '📊' },
              { id: 'stakeholders', label: 'Stakeholders', icon: '👥' },
              { id: 'channels', label: 'Channels', icon: '📱' },
              { id: 'impact', label: 'Impact', icon: '🌍' },
              { id: 'roi', label: 'ROI', icon: '💰' },
              { id: 'analytics', label: 'Analytics', icon: '📈' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-6 py-4 font-medium transition-all border-b-2 ${
                  activeTab === tab.id
                    ? 'text-purple-600 border-purple-600 bg-purple-50'
                    : 'text-gray-600 border-transparent hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className="text-xl">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'stakeholders' && renderStakeholders()}
        {activeTab === 'channels' && renderChannels()}
        {activeTab === 'impact' && renderImpact()}
        {activeTab === 'roi' && renderROI()}
        {activeTab === 'analytics' && renderAnalytics()}
      </div>
    </div>
  );
};

export default ESGEngagementInsights;
